import {
  eventTitle,
  localDate,
  type CalendarBirthday,
  type CalendarMonthEvent,
  type CalendarMonthResponse,
  type PublicUser,
} from '@veriverse/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../net/api.js';
import {
  birthdayDayOptions,
  entriesByDay,
  monthGrid,
  monthTitle,
  shiftMonth,
  weekdayNames,
  type YearMonth,
} from '../state/birthday-calendar.js';
import { useSessionStore } from '../state/session-store.js';
import { OFFICE_TIME_ZONE } from '../state/welcome-weather.js';

/**
 * Birthday calendar (spec §6.13): a month view of visible birthdays and admin-created company
 * events, plus the user's own birthday setting.
 */
export function CalendarPanel({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const [today] = useState(() => localDate(new Date(), OFFICE_TIME_ZONE));
  const [shown, setShown] = useState<YearMonth>({ year: today.year, month: today.month });
  const [birthdays, setBirthdays] = useState<CalendarBirthday[]>([]);
  const [events, setEvents] = useState<CalendarMonthEvent[]>([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    let current = true;
    api<CalendarMonthResponse>(`/api/calendar?year=${shown.year}&month=${shown.month}`)
      .then((body) => {
        if (!current) return;
        setBirthdays(body.birthdays);
        setEvents(body.events);
        setError(false);
      })
      .catch(() => {
        if (current) setError(true);
      });
    return () => {
      current = false;
    };
  }, [shown]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isCurrentMonth = shown.year === today.year && shown.month === today.month;
  const byDay = entriesByDay(birthdays, events);
  const title = (event: CalendarMonthEvent) => eventTitle(event.title, i18n.language);

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="panel calendar"
        role="dialog"
        aria-label={t('calendar.title')}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="calendar-header">
          <button
            type="button"
            className="secondary"
            aria-label={t('calendar.previous')}
            onClick={() => setShown((m) => shiftMonth(m, -1))}
          >
            ‹
          </button>
          <h1>{monthTitle(shown, i18n.language)}</h1>
          <button
            type="button"
            className="secondary"
            aria-label={t('calendar.next')}
            onClick={() => setShown((m) => shiftMonth(m, 1))}
          >
            ›
          </button>
        </div>

        <table className="calendar-grid">
          <thead>
            <tr>
              {weekdayNames(i18n.language).map((name) => (
                <th key={name} scope="col">
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthGrid(shown).map((week, w) => (
              <tr key={w}>
                {week.map((day, d) => {
                  if (day === null) return <td key={d} />;
                  const entry = byDay.get(day);
                  const names = entry?.birthdays ?? [];
                  const dayEvents = entry?.events ?? [];
                  const classes = [
                    isCurrentMonth && day === today.day ? 'today' : '',
                    names.length > 0 ? 'has-birthday' : '',
                    dayEvents.length > 0 ? 'has-event' : '',
                  ].filter(Boolean);
                  const tooltip = [...dayEvents.map(title), ...names].join(', ');
                  return (
                    <td
                      key={d}
                      className={classes.join(' ') || undefined}
                      title={tooltip || undefined}
                    >
                      <span className="calendar-day">{day}</span>
                      {dayEvents.length > 0 && <span aria-hidden="true"> 📅</span>}
                      {names.length > 0 && <span aria-hidden="true"> 🎂</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {error ? (
          <p className="error">{t('calendar.loadError')}</p>
        ) : (
          <>
            {events.length > 0 && (
              <section aria-label={t('calendar.events')}>
                <h2>{t('calendar.events')}</h2>
                <ul className="calendar-list">
                  {events.map((event) => (
                    <li key={event.id} className={`calendar-event ${event.type}`}>
                      <strong>{event.day}</strong> {title(event)}{' '}
                      <small className="muted">{t(`calendar.eventTypes.${event.type}`)}</small>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {birthdays.length === 0 ? (
              <p className="muted">{t('calendar.none')}</p>
            ) : (
              <ul className="calendar-list" aria-label={t('calendar.birthdays')}>
                {birthdays.map((b) => (
                  <li key={b.userId}>
                    <strong>{b.day}</strong> {b.displayName}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        <BirthdayForm />

        <button type="button" className="secondary" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}

/** The user's own birthday (month and day only) and whether colleagues may see it. */
function BirthdayForm() {
  const { t, i18n } = useTranslation();
  const user = useSessionStore((s) => s.user);
  const [month, setMonth] = useState(user?.birthday?.month ?? 0);
  const [day, setDay] = useState(user?.birthday?.day ?? 0);
  const [visible, setVisible] = useState(user?.birthdayVisible ?? false);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  const days = month ? birthdayDayOptions(month) : [];
  const monthName = (m: number) =>
    new Intl.DateTimeFormat(i18n.language === 'sv' ? 'sv-SE' : 'en-GB', {
      month: 'long',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(2028, m - 1, 1)));

  const save = async (body: Record<string, unknown>) => {
    try {
      const updated = await api<PublicUser>('/api/me/preferences', { method: 'PATCH', body });
      useSessionStore.getState().setUser(updated);
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!month || !day) return;
    void save({ birthday: { month, day }, birthdayVisible: visible });
  };

  const onRemove = () => {
    setMonth(0);
    setDay(0);
    setVisible(false);
    void save({ birthday: null, birthdayVisible: false });
  };

  return (
    <form className="birthday-form" onSubmit={onSubmit}>
      <h2>{t('calendar.mine')}</h2>
      <div className="row">
        <select
          aria-label={t('calendar.month')}
          value={month}
          onChange={(event) => {
            const next = Number(event.target.value);
            setMonth(next);
            setDay((current) => (current > birthdayDayOptions(next).length ? 0 : current));
            setStatus('idle');
          }}
        >
          <option value={0}>{t('calendar.month')}</option>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>
              {monthName(m)}
            </option>
          ))}
        </select>
        <select
          aria-label={t('calendar.day')}
          value={day}
          disabled={!month}
          onChange={(event) => {
            setDay(Number(event.target.value));
            setStatus('idle');
          }}
        >
          <option value={0}>{t('calendar.day')}</option>
          {days.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={visible}
          onChange={(event) => {
            setVisible(event.target.checked);
            setStatus('idle');
          }}
        />
        {t('calendar.visible')}
      </label>
      <p className="muted">{t('calendar.privacy')}</p>
      <div className="row">
        <button type="submit" disabled={!month || !day}>
          {t('calendar.save')}
        </button>
        {user?.birthday && (
          <button type="button" className="secondary" onClick={onRemove}>
            {t('calendar.remove')}
          </button>
        )}
        {status === 'saved' && <span role="status">{t('calendar.saved')}</span>}
        {status === 'error' && <span className="error">{t('calendar.saveError')}</span>}
      </div>
    </form>
  );
}
